import * as React from "react";

import * as R from "ramda";

import classNames from "classnames";

import Confetti from "react-confetti";

import { backgroundStyleFromNode } from "../../../helpers/Doc/changes";
import { displayModes } from "../../../helpers/Doc/displayMode";
import { currentTimeInSeconds } from "../../../helpers/time_format";

import DocCDQFeatures from "./CDQFeatures";
import DocSectionHeader from "./Section/Header";

import DocBlockNodes from "../BlockNodes";
import IntegrationsTags from "../../Integrations/component";

import { DocFeedbackModal } from "../Modals/FeedbackModal";
import FinishCard from "./Section/FinishCard";
import FinishCardModal from "./Section/Modals/FinishCardModal";
import QuizModal from "./Section/Modals/QuizModal";
import LowTimeModal from "./Section/Modals/LowTimeModal";
import CompletionModal from "./Section/Modals/CompletionModal";

declare global {
  interface Window {
    Turbo: any;
  }
}

interface DocSectionProps extends DocSectionType<InlineNode>, BlockNodeAll {
  blockNodesContainsSelection?: boolean;
  currentMenu?: DocMenu;
  integrationInfo: IntegrationInfo;
  openSiblingSectionUIDs: Array<string>;
  paused?: boolean;
  roData: RoData;
  roDocumentCount: number;
  submitted?: boolean;
}

interface DocSectionState {
  timeLeftInSeconds?: number;
  showModal: boolean;
  lowTimeModal: boolean;
  completionModal: boolean;
}

export default class DocSection extends React.PureComponent<
  DocSectionProps,
  DocSectionState
> {
  constructor(props: DocSectionProps) {
    super(props);
    // Initialize with default values to avoid hydration mismatch
    // localStorage will be read in componentDidMount
    let startingState: DocSectionState = {
      showModal: false,
      lowTimeModal: false,
      completionModal: false,
    };

    const timeLeft = this.calculateTimeLeft();
    if (timeLeft) {
      startingState = { ...startingState, timeLeftInSeconds: timeLeft };
    }

    this.state = startingState;
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleAcknowledgeLowTime = this.handleAcknowledgeLowTime.bind(this);
  }

  private sectionRef: React.RefObject<HTMLDivElement> =
    React.createRef<HTMLDivElement>();
  private timer?: ReturnType<typeof setTimeout>;
  private oldBoundingClientRect?: DOMRect;
  private examEndActionHandled = false;
  private examEndSubmitInFlight = false;

  componentDidMount() {
    // Read from localStorage after component mounts to avoid hydration mismatch
    if (typeof window !== 'undefined' && window.localStorage) {
      this.setState({
        showModal: localStorage.getItem("showModal") === "true",
        lowTimeModal: localStorage.getItem("lowTimeModal") === "true",
        completionModal: localStorage.getItem("completionModal") === "true",
      });
    }

    if (!this.props.userContext.isAdmin) {
      this.startTimer();
    }
    document.addEventListener(
      "acknowledgeLowTime",
      this.handleAcknowledgeLowTime
    );
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    document.removeEventListener(
      "acknowledgeLowTime",
      this.handleAcknowledgeLowTime
    );
  }

  startTimer() {
    if (this.props.timeLimit) {
      if (
        !this.props.currentTimeSegmentStart &&
        this.props.timeUsedBeforeCurrentSegment &&
        this.props.timeUsedBeforeCurrentSegment > 0
      ) {
        this.props.userContext.addState({
          [this.props.uid]: {
            currentTimeSegmentStart: Math.floor(new Date().getTime() / 1000),
          },
        });
      }

      this.timer = setInterval(this.tick.bind(this), 1000);
    }
  }

  calculateTimeLeft() {
    if (this.props.timeLimit) {
      let timeLeft = this.props.timeLimit;
      if (this.props.timeUsedBeforeCurrentSegment) {
        timeLeft = timeLeft - this.props.timeUsedBeforeCurrentSegment;
      }

      if (this.props.currentTimeSegmentStart) {
        timeLeft =
          timeLeft -
          (currentTimeInSeconds() - this.props.currentTimeSegmentStart);
      }

      return timeLeft;
    } else {
      return;
    }
  }

  tick() {
    if (this.props.timeLimit && this.props.currentTimeSegmentStart) {
      this.setState({ timeLeftInSeconds: this.calculateTimeLeft() });
    }
  }

  componentWillUpdate(nextProps: DocSectionProps, nextState: DocSectionState) {
    if (this.props.collapsed !== nextProps.collapsed) {
      this.oldBoundingClientRect =
        this.sectionRef.current?.getBoundingClientRect();
    }
  }

  componentDidUpdate(prevProps: DocSectionProps, prevState: DocSectionState) {
    if (prevProps.collapsed !== this.props.collapsed && this.props.collapsed) {
      const oldBoundingClientRect = this.oldBoundingClientRect;
      const newBoundingClientRect =
        this.sectionRef.current?.getBoundingClientRect();
      if (oldBoundingClientRect && newBoundingClientRect) {
        if (oldBoundingClientRect.bottom < 0) {
          window.scrollBy(
            0,
            newBoundingClientRect.height - oldBoundingClientRect.height
          );
        } else if (oldBoundingClientRect.top < 0) {
          if (this.props.collapsed) {
            window.scrollBy(0, newBoundingClientRect.bottom - 70);
          }
        }
      }
    }
  }

  // Function to update states
  handleShowModal = () => {
    const percentElement = document.getElementById(
      "percentRecommendedTimeField"
    ) as HTMLInputElement;
    const percentOfRecommendedTime = parseFloat(percentElement?.value || "100");

    if (percentOfRecommendedTime < 25) {
      localStorage.setItem("lowTimeModal", "true");
      this.setState({
        lowTimeModal: true,
      });
    } else {
      localStorage.setItem("completionModal", "true");
      localStorage.removeItem("showModal");
      localStorage.removeItem("lowTimeModal");
      this.setState({
        completionModal: true,
        showModal: false,
        lowTimeModal: false,
      });

      if (
        this.props.userContext.documentSetListForStudentChoiceCases.length === 0
      ) {
        setTimeout(() => {
          document.dispatchEvent(new Event("acknowledgeLowTime"));
        }, 5000);
      }
    }
  };

  handleAcknowledgeLowTime = () => {
    localStorage.removeItem("lowTimeModal");
    localStorage.setItem("showModal", "true");
    localStorage.removeItem("completionModal");
    this.setState({
      lowTimeModal: false,
      showModal: true,
      completionModal: false,
    });
  };

  private getFinishCardProps() {
    const { documentContext } = this.props;

    return {
      quizUrl: documentContext.quizUrl?.toString() || "",
      quizQuestionCount: documentContext.quizQuestionCount,
      todoUrl: documentContext.todoUrl?.toString() || "",
      todoAction: documentContext.todoAction?.toString() || "",
      caseSummaryUrl: documentContext.caseSummaryUrl?.toString() || "",
      title: documentContext.title,
      timeOnCase: documentContext.timeOnCase?.toString() || "",
    };
  }

  private getLowTimeModalProps() {
    const { displayContext, documentContext, userContext } = this.props;

    return {
      resetCaseUrl: displayContext.resetCaseUrl?.toString() || "",
      programName: displayContext.programName?.toString() || "",
      timeOnCase: documentContext.timeOnCase?.toString() || "",
      userId: userContext.id,
      newCourseBuilder2Enabled: true,
      documentSetListForStudentChoiceCases:
        userContext.documentSetListForStudentChoiceCases,
      documentId: documentContext.id,
    };
  }

  private getFinishCaseFinalContent() {
    const finishCardProps = this.getFinishCardProps();
    const lowTimeModalProps = this.getLowTimeModalProps();

    if (this.state.lowTimeModal) {
      return <LowTimeModal {...lowTimeModalProps} />;
    }

    if (this.state.completionModal) {
      return (
        <>
          <FinishCard {...finishCardProps} />
          <CompletionModal
            newCourseBuilder2Enabled={true}
            documentSetListForStudentChoiceCases={
              this.props.userContext.documentSetListForStudentChoiceCases
            }
            documentId={this.props.documentContext.id}
          />
        </>
      );
    }

    if (this.state.showModal) {
      return (
        <>
          <FinishCard {...finishCardProps} />
          <FinishCardModal {...finishCardProps} />
        </>
      );
    }

    return (
      <>
        <FinishCard {...finishCardProps} />
        <QuizModal />
      </>
    );
  }

  render() {
    const { title, displayContext, userContext, content } = this.props;

    const isFeedbackCard = this.props.classes?.includes("feedback_card");
    if (isFeedbackCard && displayContext.displayMode === displayModes.STUDENT) {
      const button = content.find(
        (child): child is DocButtonType<InlineNode> => child.type == "button"
      );

      return (
        <DocFeedbackModal
          displayContext={displayContext}
          userContext={userContext}
          onClick={button.onClick}
          onSubmitFinish={this.handleShowModal}
        />
      );
    }

    if (!this.props.userContext.isAdmin) {
      if (this.props.submitted) {
        clearInterval(this.timer);
        if (!this.examEndActionHandled && !this.examEndSubmitInFlight) {
          this.examEndSubmitInFlight = true;
          void Promise.resolve(
            this.props.userContext.submitExam(
              "Your exam has been successfully submitted.",
            ),
          )
            .then((ok) => {
              if (ok) this.examEndActionHandled = true;
            })
            .finally(() => {
              this.examEndSubmitInFlight = false;
            });
        }
        return null;
      } else if (
        this.props.timeLimit &&
        this.state.timeLeftInSeconds &&
        this.state.timeLeftInSeconds <= 0
      ) {
        clearInterval(this.timer);
        if (!this.examEndActionHandled && !this.examEndSubmitInFlight) {
          this.examEndSubmitInFlight = true;
          void Promise.resolve(
            this.props.userContext.submitExam(
              "Your time has expired. Your exam has been successfully submitted.",
            ),
          )
            .then((ok) => {
              if (ok) this.examEndActionHandled = true;
            })
            .finally(() => {
              this.examEndSubmitInFlight = false;
            });
        }
        return null;
      } else if (this.props.paused) {
        if (!this.examEndActionHandled) {
          this.examEndActionHandled = true;
          this.props.userContext.pauseExam();
        }
        return null;
      }
    }

    let visibleContent = this.props.content;
    let shouldDisplayIntegrationTags =
      this.props.classes?.includes("case_synopsis_card") &&
      this.props.featureFlag.integrations;

    if (!this.props.displayContext.isDisplayFull) {
      if (
        (this.props.level !== 0 && !this.props.nested) ||
        this.props.collapsed ||
        this.props.inactiveBool
      ) {
        visibleContent = [];
        shouldDisplayIntegrationTags = false;
      }
    }

    if (!this.props.displayContext.isUngated) {
      visibleContent = visibleContent.filter(
        (node) => node && node.invisible !== true
      );
    }

    if (this.props.displayContext.onlyDisplaySectionTitles) {
      visibleContent = visibleContent.filter((node) => node.type === "section");
      shouldDisplayIntegrationTags = false;
    }

    let openSiblingSectionUIDs;
    if (this.props.level === 0 && !this.props.displayContext.canBeEditing) {
      openSiblingSectionUIDs = visibleContent
        .filter((node) => {
          return (
            node.type === "section" &&
            node.collapsed !== true &&
            node.collapsible === true
          );
        })
        .map((node) => node.uid);
    }

    const classes = classNames(
      "doc-section",
      this.props.uid,
      this.props.classes,
      {
        nested: this.props.nested,
        collapsible: this.props.collapsible,
        collapsed: this.props.collapsed,
        inactive: this.props.inactiveBool,
        "focused-bookmark": this.props.isFocusedBookmark,
      }
    );

    const isSummaryStatementQuestion = visibleContent.find(
      (node) => node.type === "section" && node.classes?.includes("summary_statement_question")
    );
    const isSummaryStatementSection =
      typeof title === "string" && /^Summary statement/i.test(title);
    const isSaqCard = this.props.classes?.includes("saq_card");
    const isFinishCaseCard = this.props.classes?.includes("finish_case_card");
    let propsClone = { ...this.props };

    if (
      displayContext.enableAiSummaryStatementFeedback &&
      (isSummaryStatementSection || isSummaryStatementQuestion)
    ) {
      const questionSectionIndex = visibleContent.findIndex(
        (node) => node.type == "section" && node.classes?.includes("question")
      );
      const questionSection = visibleContent.find(
        (node): node is DocSectionType<InlineNode> =>
          node.type === "section" && !!node.classes?.includes("question")
      );

      const answerCommentIndex = questionSection?.content?.findIndex(
        (node): node is DocSectionType<InlineNode> =>
          node.type === "section" && !!node.classes?.includes("answer_comment")
      );
      const answerCommentSection = questionSection?.content?.find(
        (node): node is DocSectionType<InlineNode> =>
          node.type === "section" && !!node.classes?.includes("answer_comment")
      );
      const idealSummaryStatement = answerCommentSection?.content?.find(
        (node): node is DocParagraphType<InlineNode> =>
          node.type === "paragraph" &&
          !/<strong>Expert Answer:<\/strong>/.test((node as any).content)
      )?.content; // TODO: Remove any
      const summaryStatementTextInput = questionSection?.content?.find(
        (node): node is DocTextInputType => node.type === "textInput"
      );
      const summaryStatementSubmitButtonIndex =
        questionSection?.content?.findIndex(
          (node): node is DocButtonType<InlineNode> => node.type === "button"
        );
      const summaryStatementSubmitButton = questionSection?.content?.find(
        (node): node is DocButtonType<InlineNode> => node.type === "button"
      );

      if (
        idealSummaryStatement &&
        summaryStatementTextInput &&
        summaryStatementSubmitButton
      ) {
        const summaryStatementSubmitButtonActions =
          summaryStatementSubmitButton.onClick;

        visibleContent = R.over(
          R.lensPath([questionSectionIndex, "content"]),
          R.remove(answerCommentIndex, 1),
          visibleContent
        );
        visibleContent = R.over(
          R.lensPath([questionSectionIndex, "content"]),
          R.remove(summaryStatementSubmitButtonIndex, 1),
          visibleContent
        );

        summaryStatementTextInput.aiAnswerCommentSection = answerCommentSection;
        summaryStatementTextInput.aiSummaryStatementIdeal =
          idealSummaryStatement;
        summaryStatementTextInput.aiSummaryStatementAllowFeedback = true;
        summaryStatementTextInput.aiSubmitButtonActions =
          summaryStatementSubmitButtonActions;
      }
    }

    if (isSaqCard) {
      return <></>;
    }

    if (isFinishCaseCard) {
      propsClone = { ...propsClone, title: "Next Steps" };
    }

    const hideSectionBodyFromAssistiveTech =
      !this.props.displayContext.isDisplayFull &&
      (this.props.collapsed || this.props.inactiveBool);

    return (
      <div
        id={this.props.uid}
        data-is-node
        data-uid={this.props.uid}
        className={classes}
        style={backgroundStyleFromNode(this.props)}
        contentEditable={false}
        ref={this.sectionRef}
      >
        <DocSectionHeader {...propsClone} />
        {this.state.completionModal && (
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            style={{ position: "fixed", top: 0, left: 0 }}
          />
        )}
        <div id={`${this.props.uid}_section_body`} className="doc-section-body" aria-hidden={hideSectionBodyFromAssistiveTech || undefined}>
          <DocCDQFeatures {...this.props} />
          <DocBlockNodes
                {...this.props}
                level={this.props.level}
                content={visibleContent}
                contentKey="content"
                independentlyEditable
                openSiblingSectionUIDs={openSiblingSectionUIDs}
                shouldHaveAddNodes={
                  this.props.editingContext.isEditing &&
                  (this.props.displayContext.isDisplayFull || !this.props.collapsed)
                }
              />
          {shouldDisplayIntegrationTags && (
            <IntegrationsTags {...this.props.integrationInfo} />
          )}
          {isFinishCaseCard && this.getFinishCaseFinalContent()}
        </div>
      </div>
    );
  }
}
